import { useState } from 'react';
import styled, { css } from 'styled-components';
import type { MulticolorValue } from '@elfsight/configurator-contracts';
import { Button, TextControl } from '@elfsight/widget-ui';
import { useSettings } from '@wu/widget-core';
import { meta } from '../../../constants';
import { useChannels } from '../../channels';
import { getChatIcon, getInvertedColor, useKeyEvents } from '../../helpers';
import { getPrefilledLink, useButtonOpenLink, useTextMessage } from '../utils';

const FONT_SIZE = 15;
const BUTTON_SIZE = 28;
const BUTTON_ICON_SIZE = 16;
const BUTTON_PADDING = (BUTTON_SIZE - BUTTON_ICON_SIZE) / 2;
const LINE_HEIGHT = 1.33;

export const MessageField = () => {
  const {
    messageButtonColor: derivedMessageButtonColor,
    messagePlaceholder,
    messageFieldColor
  } = useSettings();
  const [{ link }] = useChannels();
  const [focus, setFocus] = useState(false);
  const [message, setMessage] = useState('');
  const textMessage = useTextMessage({
    message
  });
  const messageButtonColor =
    derivedMessageButtonColor === 'object'
      ? ((derivedMessageButtonColor as MulticolorValue).color as string)
      : (derivedMessageButtonColor as string);
  const isWhatsAppShortLink = link?.url.includes('wa.me/message');
  const prefilledLink = getPrefilledLink(
    link,
    !isWhatsAppShortLink ? textMessage : ''
  );

  const placeholderColor = getInvertedColor({
    color: messageFieldColor,
    opacity: 0.3
  });
  const borderColor = getInvertedColor({
    color: messageFieldColor,
    opacity: 0.1
  });
  const fieldTextColor = getInvertedColor({ color: messageFieldColor });
  const iconColor = getInvertedColor({
    color: messageButtonColor
  });
  const placeholderText = messagePlaceholder || 'Enter Your Message...';

  const opener = useButtonOpenLink({ ...prefilledLink, title: meta.name });

  useKeyEvents('Enter', (e) => {
    if (focus) {
      e.preventDefault();
      opener();
    }
  });

  return (
    <MessageContainer
      _backgroundColor={messageFieldColor}
      _borderColor={borderColor}
    >
      <MessageTextContainer
        _scrollBarColor={borderColor}
        _hasPlaceholder={!message}
      >
        <TextControl
          placeholder={placeholderText}
          placeholderColor={placeholderColor}
          color={fieldTextColor}
          font={{
            fontSize: FONT_SIZE
          }}
          lineHeight={LINE_HEIGHT}
          padding={[3, 0]}
          minVisibleRows={1}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          onValueChange={(value) => setMessage(value)}
          multiline
        />
      </MessageTextContainer>

      <MessageButtonContainer>
        <Button
          width={BUTTON_SIZE}
          horizontalPadding={BUTTON_PADDING}
          verticalPadding={BUTTON_PADDING}
          borderRadius={BUTTON_SIZE / 2}
          iconRight={getChatIcon('sendMessage')}
          color={messageButtonColor}
          onClick={opener}
          fontColor={iconColor}
          borderWidth={0}
          iconOnly
          style={{
            overflow: 'hidden'
          }}
        />
      </MessageButtonContainer>
    </MessageContainer>
  );
};

const MessageContainer = styled.div<{
  _borderColor?: string;
  _backgroundColor?: string;
}>`
  display: flex;
  padding: 12px 2px 12px 20px;
  overflow: hidden;
  position: relative;
  border-top: ${({ _borderColor }) => `1px solid ${_borderColor}`};
  background-color: ${({ _backgroundColor }) => _backgroundColor};
`;

const MessageTextContainer = styled.div<{
  _scrollBarColor?: string;
  _hasPlaceholder?: boolean;
}>`
  overflow: scroll;
  min-height: 28px;
  max-height: 64px;
  padding-right: 42px;
  width: 100%;

  &::-webkit-scrollbar {
    width: 6px;
    height: 100%;
  }

  &::-webkit-scrollbar-track {
    display: none;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 3px;
    background-color: ${({ _scrollBarColor }) => _scrollBarColor};
  }

  ${({ _hasPlaceholder }) =>
    _hasPlaceholder &&
    css`
      & textarea {
        max-height: 20px;
        overflow: hidden;
      }
    `}
`;

const MessageButtonContainer = styled.div`
  position: absolute;
  bottom: 12px;
  right: 12px;
  transition: right 0.3s;

  & svg {
    transform: scale(1.8);
  }
`;
